import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useToast,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  BrregOrgDto,
  MinimalOrgDto,
  OrganizationEntryDto,
  useCreateOrganizationEntryMutation,
  useSearchOrganizationsAndEntriesQuery,
} from "../../../../autogen/bff-api";
import { toPostalAddress } from "../../../../common/brreg/brregAddress";
import { BrregSuggestionList } from "../../../../common/brreg/BrregSuggestionList";
import { useBrregSearch } from "../../../../common/brreg/useBrregSearch";
import { useApiError } from "../../../../common/errors/useApiError";
import { useBaseData } from "../../../../common/useBaseData";
import { Country } from "./Country";
import { OrganizationNumber } from "./OrganizationNumber";

interface FormValues {
  name: string;
  country: string;
  organizationNumber: string;
  street: string;
  postCode: string;
  city: string;
}

interface ModalProps {
  onConfirm: (newEntry: OrganizationEntryDto) => Promise<void>;
  onSelectExisting?: (existing: MinimalOrgDto) => void | Promise<void>;
  onClose: () => void;
}

export const CreateOrganizationEntryModal = ({ onConfirm, onSelectExisting, onClose }: ModalProps) => {
  const toast = useToast();
  const { t } = useTranslation();
  const baseData = useBaseData();
  const [create, { isLoading }] = useCreateOrganizationEntryMutation();
  const apiErrorDisplayer = useApiError();
  const norwayCountryId = baseData.countries.find((c) => c.alpha3Code === "NOR")?.id;
  const methods = useForm<FormValues>({ defaultValues: { country: norwayCountryId } });
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = methods;

  const selectedCountryId = watch("country");
  const selectedOrgNumber = watch("organizationNumber");
  const name = watch("name") ?? "";

  const [isNameFocused, setIsNameFocused] = useState(false);
  const lastSelectedNameRef = useRef<string | null>(null);
  const isAfterSelection = lastSelectedNameRef.current !== null && name === lastSelectedNameRef.current;

  const nameSearch = useBrregSearch({
    countryId: selectedCountryId,
    query: name,
    enabled: isNameFocused && !isAfterSelection,
  });

  const { data: existingMatches } = useSearchOrganizationsAndEntriesQuery(
    { organizationNumber: selectedOrgNumber, limit: 5 },
    { skip: !selectedOrgNumber }
  );
  const existing = existingMatches?.organizations?.[0];

  const applyBrregOrg = (org: BrregOrgDto) => {
    const address = toPostalAddress(org);
    lastSelectedNameRef.current = org.name;
    setValue("name", org.name, { shouldValidate: true });
    setValue("organizationNumber", org.organizationNumber, { shouldValidate: true });
    if (norwayCountryId) setValue("country", norwayCountryId, { shouldValidate: true });
    setValue("street", address.street);
    setValue("postCode", address.postCode);
    setValue("city", address.city);
    setIsNameFocused(false);
  };

  const nameField = register("name", {
    required: t("Please provide a company name") ?? "",
    minLength: { value: 2, message: t("Must be at least 2 characters long") },
    onChange: () => {
      lastSelectedNameRef.current = null;
    },
  });

  const submit = async (values: FormValues) => {
    try {
      const orgEntry = await create({
        createOrganizationEntryRequest: {
          name: values.name,
          industryIds: [],
          countryId: values.country,
          organizationNumber: values.organizationNumber,
          street: values.street || null,
          postCode: values.postCode || null,
          city: values.city || null,
        },
      }).unwrap();
      toast({ title: t("New organization entry created!"), status: "success", position: "top-right" });
      await onConfirm(orgEntry);
      onClose();
    } catch (e) {
      apiErrorDisplayer.trigger(e);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("Add unregistered company")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(submit)}>
              <FormControl isInvalid={errors.name !== undefined}>
                <FormLabel htmlFor="name">{t("Company name")}</FormLabel>
                <Box position="relative">
                  <Input
                    id="name"
                    autoComplete="off"
                    placeholder={t("Search by company name or organization number") ?? ""}
                    {...nameField}
                    onFocus={() => setIsNameFocused(true)}
                    onBlur={(e) => {
                      nameField.onBlur(e);
                      window.setTimeout(() => setIsNameFocused(false), 150);
                    }}
                  />
                  {isNameFocused && !isAfterSelection && (
                    <BrregSuggestionList result={nameSearch} onSelect={applyBrregOrg} />
                  )}
                </Box>
                <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
              </FormControl>
              {existing && (
                <Alert status="warning" mt="15px" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertDescription>
                      {t("This company is already registered in SourceMagnet.")}
                    </AlertDescription>
                    {onSelectExisting && (
                      <Button
                        variant="link"
                        colorScheme="blue"
                        fontWeight="bold"
                        fontSize="sm"
                        mt="1"
                        onClick={async () => {
                          await onSelectExisting(existing);
                          onClose();
                        }}
                      >
                        {t("Select existing")} ({existing.name})
                      </Button>
                    )}
                  </Box>
                </Alert>
              )}
              <Box mt="15px">
                <Country />
              </Box>
              <Box mt="15px">
                <OrganizationNumber />
              </Box>
              <FormControl mt="15px">
                <FormLabel htmlFor="street">{t("Street address")}</FormLabel>
                <Input id="street" {...register("street")} />
              </FormControl>
              <Flex mt="15px" gap="10px">
                <FormControl>
                  <FormLabel htmlFor="postCode">{t("Post code")}</FormLabel>
                  <Input id="postCode" {...register("postCode")} />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="city">{t("City")}</FormLabel>
                  <Input id="city" {...register("city")} />
                </FormControl>
              </Flex>
              <Flex mt="30px" justifyContent={"end"} mb="10px">
                <Button type="submit" colorScheme="teal" isLoading={isLoading}>
                  {t("Add organization")}
                </Button>
              </Flex>
            </form>
          </FormProvider>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
